import React, { useState, useCallback, useEffect } from 'react';

import dashboardApi from 'services/apis/dashboard';

import { useSnackbar } from 'v3/components/Snackbar';
import moment from 'moment';
import { useQuery } from 'hooks';
import { Table, Text, Link, Button } from 'v3/components';
import { formatDateTime } from 'v3/utils/formatter';
import Card, { Number, Description, SeeAll } from './Card';

export default function ApprovedTravels({ dashboard, name, id }) {
  const [isFetching, setFetching] = useState(false);
  const [data, setData] = useState({});
  const [filter, setFilter] = useState({});
  const [currentPage, setCurrentPage] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [daysFromNow, setDaysFromNow] = useState(3);

  const snackbar = useSnackbar();
  const queryParams = useQuery();

  useEffect(() => {
    setDaysFromNow(moment(new Date()).diff(filter.start_date, 'days'));
  }, [filter.start_date]);

  function setQueryParams(filters) {
    const { tags = [], start_date, end_date } = filters;

    queryParams.delete('tags');

    if (tags) {
      tags.forEach((tag) => {
        queryParams.append('tags', tag.id);
      });
    }

    if (start_date) {
      queryParams.set('start_date', start_date);
    }

    if (end_date) {
      queryParams.set('end_date', end_date);
    }

    queryParams.set('status', 2);

    if (tags?.length === 0) {
      queryParams.set('no-tags', true);
    } else {
      queryParams.delete('no-tags');
    }
  }
  const fetchTravels = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      const {
        tags = [],
        clients = [],
        negotiators = [],
        costCenters = [],
        recuseCostCenters = [],
        pcps = [],
        companies = [],
        page = 1,
        start_date,
        end_date,
      } = filters;

      if (page === 1) {
        setFetching(true);
      }

      setQueryParams(filters);
      setFilter(filters);
      setCurrentPage(+page);

      const response = await dashboardApi.get(`/loads-attendeds-pending-sm`, {
        params: {
          tags: tags?.map((tag) => tag.id),
          clients: clients?.map((client) => client.id),
          start_date,
          negotiators: negotiators?.map((negotiator) => negotiator?.id),
          costCenters: costCenters?.map((costCenter) => costCenter?.id),
          recuseCostCenters: recuseCostCenters?.map((costCenters) => costCenters?.id),
          pcps: pcps?.map((pcp) => pcp?.id),
          companies: companies?.map((company) => company?.id),
          end_date: end_date || undefined,
          page,
        },
      });

      setPagination(response?.data?.last?.pagination);

      if (page === 1) {
        setData(response.data);
      } else {
        setData((oldData) => ({
          ...oldData,
          last: {
            ...oldData.last,
            data: [...oldData.last.data, ...response.data.last.data],
          },
        }));
      }
    } catch (ex) {
      snackbar.show(<Text>Erro ao carregar últimas viagens</Text>, {
        type: 'error',
      });
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, []);

  return (
    <Card
      title="Viagens rastreadas aprovadas sem SM"
      isLoading={isFetching}
      fetchFn={fetchTravels}
      onFiltersChanged={fetchTravels}
      excludeFilterByRecuseCostCenters={false}
      dashboard={dashboard}
      name={name}
      excludeFilterByBranchOffice
      id={id}
      ListComponent={
        <>
          <Table striped>
            <thead>
              <tr>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Troca de Nota
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark" type="regular">
                    Centro de custo
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark" type="regular">
                    Empresa
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark" type="regular">
                    Tags da carga
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark" type="regular">
                    Viagem
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark" type="regular">
                    Carregamento
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark" type="regular">
                    Origem
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark" type="regular">
                    Destino
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark" type="regular">
                    Cliente
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark" type="regular">
                    Motorista
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark" type="regular">
                    Veículo
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark" type="regular">
                    Usuário que aprovou
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.last?.data?.map((travel) => {
                const title = `Cliente ${travel.social_name} - ${travel.cgccpf}`;

                return (
                  <tr>
                    <td>
                      <Text type="regular" color="gray" truncate>
                        {travel?.replacement_order
                          ? `${travel.replacement_order}º Troca Nota`
                          : 'Viagem Normal'}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray" truncate>
                        {travel.cost_center || '-'}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray" truncate>
                        {travel?.company_name || '-'}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray" truncate>
                        {travel.load_tags || '-'}
                      </Text>
                    </td>
                    <td>
                      <Link to={`/viagens/${travel.attended_id}`} title={title} target="_blank">
                        {travel.attended_id}
                      </Link>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {formatDateTime(travel.scheduled_origin)}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray" truncate>
                        {travel.city_origin} - {travel.uf_origin}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray" truncate>
                        {travel.city_destination} - {travel.uf_destination}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray" truncate>
                        {travel.social_name}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray" truncate>
                        {travel.driver_name}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {travel.plate}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {travel.risk_manager_user}
                      </Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {pagination?.lastPage > 1 && pagination?.to < pagination?.total && (
            <div className="text-center mt-3 col-12">
              <Button
                className="mt-2 ml-auto align-item-center"
                onClick={() => {
                  fetchTravels({
                    page: currentPage + 1,
                    tags: filter?.tags,
                    clients: filter?.clients,
                    start_date: filter?.start_date,
                    end_date: filter?.end_date,
                    costCenters: filter?.costCenters,
                    recuseCostCenters: filter?.recuseCostCenters,
                    pcps: filter?.pcps,
                    companies: filter?.companies,
                    negotiators: filter?.negotiators,
                  });
                }}
                loading={loading}
              >
                <Text type="regular" color="white" truncate>
                  Ver mais
                </Text>
              </Button>
            </div>
          )}
          {!!data.total && (
            <SeeAll
              to={{
                pathname: '/viagens',
                search: queryParams.toString(),
              }}
            >
              ver todas as {data.total} viagens
            </SeeAll>
          )}
        </>
      }
      NumericComponent={
        <div className="d-flex justify-content-center text-center flex-column">
          <Number color="warning">{data?.total}</Number>
          <Description>
            aprovadas porém sem SM preenchido
            {daysFromNow > 1 && daysFromNow < 61
              ? ` nos últimos ${daysFromNow} dias`
              : ` nos últimos ${filter.start_date} dias`}
          </Description>
        </div>
      }
    />
  );
}
